import type { Meal } from "@/components/nutrition/mealColumns";
import type { Workout } from "@/components/workouts/workoutsColumns";
import {
  ensureWorkoutSections,
  groupsForSection,
} from "@/lib/utils/workoutSections";
import type {
  MealRevisionSnapshot,
  TrainingRevisionSnapshot,
} from "@/lib/planHistory/hydrateRevision";

const isUuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );

const asUuid = (value: string | null | undefined) =>
  value && isUuid(value) ? value : null;

const PLACEHOLDER_PLAN_ID = "00000000-0000-0000-0000-000000000000";

export const liveMealSnapshotFromEditor = ({
  planId,
  name,
  meals,
}: {
  planId: string | null | undefined;
  name: string;
  meals: Meal[];
}): MealRevisionSnapshot => ({
  schemaVersion: 1,
  kind: "meal_plan",
  plan: {
    id: planId && isUuid(planId) ? planId : PLACEHOLDER_PLAN_ID,
    name,
    meals: meals.map((meal, mealIndex) => ({
      id: meal.id,
      name: meal.name,
      notes: meal.notes ?? null,
      imagePath: meal.image_path ?? null,
      mealNumber: mealIndex,
      foods: meal.foods.map((food, foodIndex) => ({
        foodId: food.id,
        name: food.name,
        servingSize: food.serving_size ?? food.default_serving_size ?? 1,
        sortOrder: foodIndex,
        calories: food.calories,
        protein: food.protein,
        carb: food.carb,
        fat: food.fat,
        defaultServingSize: food.default_serving_size,
        servingType: food.serving_type,
      })),
    })),
  },
});

export const liveTrainingSnapshotFromEditor = ({
  planId,
  name,
  schedulingMode,
  workouts,
  rollingSchedule = [],
  relativeSchedule = [],
  calendarSchedule = [],
}: {
  planId: string | null | undefined;
  name: string;
  schedulingMode: "fixed" | "dated";
  workouts: Workout[];
  rollingSchedule?: {
    workout_id: string;
    day_index: number;
    order_index: number;
    rolling_period?: number;
  }[];
  relativeSchedule?: {
    id?: string;
    workout_id: string;
    day_offset: number;
    order_index: number;
  }[];
  calendarSchedule?: {
    id?: string;
    workout_id: string;
    scheduled_date: string;
    order_index: number;
  }[];
}): TrainingRevisionSnapshot => ({
  schemaVersion: 1,
  kind: "training_plan",
  plan: {
    id: planId && isUuid(planId) ? planId : PLACEHOLDER_PLAN_ID,
    name,
    description: null,
    weeks: 0,
    schedulingMode,
    workouts: workouts.map((workout, workoutIndex) => {
      const ensured = ensureWorkoutSections(workout);
      const sections = ensured.sections.map((section, sectionIndex) => {
        const groups = groupsForSection(ensured, section.id);
        const exercises = groups.flatMap(({ group, globalIndex }) =>
          group.map((exercise, subOrder) => ({
            id: asUuid(exercise.workout_exercise_id),
            exerciseId: exercise.id,
            name: exercise.name,
            position: globalIndex,
            exOrder: globalIndex,
            subOrder,
            supersetKey:
              exercise.superset_key ??
              (group.length > 1 ? `legacy:${globalIndex}` : null),
            supersetPosition:
              exercise.superset_position ?? (group.length > 1 ? subOrder : null),
            coachNote: exercise.coach_note ?? null,
            trackingTypeOverride: exercise.tracking_type_override ?? null,
            tempo: exercise.tempo ?? "0-0-0-0",
            rir: exercise.rir == null ? null : Number(exercise.rir),
            restSeconds:
              exercise.rest_time == null ? null : Number(exercise.rest_time),
            sets: (exercise.sets ?? []).map((set, setIndex) => ({
              id: asUuid(set.id),
              setNumber: setIndex + 1,
              setType: set.set_type ?? "working",
              minReps: set.startRange ?? null,
              maxReps: set.endRange ?? null,
              targetReps: null,
              targetWeight: set.targetWeight ?? null,
              targetSeconds: set.targetTimeSeconds ?? null,
              targetDistanceMeters: set.targetDistanceMeters ?? null,
              rir: set.rir ?? null,
              rpe: set.rpe ?? null,
              tempo: set.tempo ?? null,
              restSeconds: set.rest_seconds ?? null,
              notes: set.notes ?? null,
            })),
          })),
        );
        return {
          id: asUuid(section.id),
          position: section.position ?? sectionIndex,
          name: section.name,
          sectionType: section.section_type,
          isDefault: section.is_default,
          rounds: section.rounds,
          timeCapSeconds: section.time_cap_seconds,
          intervalSeconds: section.interval_seconds,
          workSeconds: section.work_seconds,
          restSeconds: section.rest_seconds,
          scoreType: section.score_type,
          notes: section.notes,
          exercises,
        };
      });
      return {
        id: workout.id,
        name: workout.name,
        description: workout.description,
        day: workout.day ?? workoutIndex,
        sections,
      };
    }),
    rollingSchedule: rollingSchedule.map((entry) => ({
        dayIndex: entry.day_index,
        orderIndex: entry.order_index,
        workoutId: entry.workout_id,
        rollingPeriod: entry.rolling_period === 14 ? 14 : 7,
      })),
    scheduleEntries: [
      ...relativeSchedule.map((entry) => ({
          id: asUuid(entry.id),
          workoutId: entry.workout_id,
          scheduleBasis: "relative" as const,
          scheduledDate: null,
          dayOffset: entry.day_offset,
          orderIndex: entry.order_index,
        })),
      ...calendarSchedule.map((entry) => ({
          id: asUuid(entry.id),
          workoutId: entry.workout_id,
          scheduleBasis: "absolute" as const,
          scheduledDate: entry.scheduled_date,
          dayOffset: null,
          orderIndex: entry.order_index,
        })),
    ],
  },
});
